import { useState, useEffect, useCallback, useMemo } from 'react';
import { WorkItem } from '../../workitems/workItemTypes';
import { LeaveEntry, AppointmentChangePayload, LeaveChangePayload } from '@techgitter/contracts';
import { getAppointments } from '../../../services/appointmentDataService';
import { getLeaveEntries } from '../../../services/leaveDataService';
import { useSignalREvent } from '../../../hooks/useSignalR';
import { formatDateStr, addDays } from '../utils/calendarUtils';
import { TechnicianColumn } from '../calendarTypes';

interface UseCalendarDataOptions {
  /** Start of visible range (Date object) */
  rangeStart: Date;
  /** End of visible range (Date object) */
  rangeEnd: Date;
}

export function useCalendarData({ rangeStart, rangeEnd }: UseCalendarDataOptions) {
  const [appointments, setAppointments] = useState<WorkItem[]>([]);
  const [leaveEntries, setLeaveEntries] = useState<LeaveEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // The backend filters appointments by their UTC `cr001_start_at` against the
  // raw from/to day strings, but the visible range is expressed in LOCAL days.
  // In Sydney (UTC+10/+11) a local-morning appointment is the previous day in
  // UTC, so a same-day UTC window would miss it entirely. Pad the fetch by one
  // day on each side; the per-day local filter (`isAppointmentOnDate`) still
  // places each appointment on the correct day, so over-fetching is harmless.
  const fromStr = useMemo(() => formatDateStr(addDays(rangeStart, -1)), [rangeStart]);
  const toStr = useMemo(() => formatDateStr(addDays(rangeEnd, 1)), [rangeEnd]);

  const loadData = useCallback(async (signal?: AbortSignal) => {
    setIsLoading(true);
    setError(null);
    try {
      const [appts, leave] = await Promise.all([
        getAppointments(fromStr, toStr, signal),
        getLeaveEntries(fromStr, toStr, signal),
      ]);
      setAppointments(appts);
      setLeaveEntries(leave);
    } catch (err: any) {
      if (err.name === 'AbortError') return;
      setError(err.message || 'Failed to load calendar data');
    } finally {
      setIsLoading(false);
    }
  }, [fromStr, toStr]);

  useEffect(() => {
    const controller = new AbortController();
    loadData(controller.signal);
    return () => controller.abort();
  }, [loadData]);

  // Real-time updates via SignalR
  useSignalREvent('appointments:changed', (data: unknown) => {
    const payload = data as AppointmentChangePayload;
    switch (payload.action) {
      case 'created':
        setAppointments(prev => [...prev, payload.appointment]);
        break;
      case 'updated':
        setAppointments(prev =>
          prev.map(a => (a.guid === payload.appointment.guid ? payload.appointment : a)),
        );
        break;
      case 'deleted':
        setAppointments(prev => prev.filter(a => a.guid !== payload.appointmentId));
        break;
    }
  });

  useSignalREvent('leave:changed', (data: unknown) => {
    const payload = data as LeaveChangePayload;
    switch (payload.action) {
      case 'created':
        setLeaveEntries(prev => [...prev, payload.leave]);
        break;
      case 'updated':
        setLeaveEntries(prev =>
          prev.map(l => (l.uuid === payload.leave.uuid ? payload.leave : l)),
        );
        break;
      case 'deleted':
        setLeaveEntries(prev => prev.filter(l => l.uuid !== payload.leaveId));
        break;
    }
  });

  // Group by technician
  const technicianColumns = useMemo((): TechnicianColumn[] => {
    const techMap = new Map<string, TechnicianColumn>();

    for (const appt of appointments) {
      const name = appt.userFullName || 'Unassigned';
      if (!techMap.has(name)) {
        techMap.set(name, { userFullName: name, appointments: [], leaveEntries: [] });
      }
      techMap.get(name)!.appointments.push(appt);
    }

    for (const leave of leaveEntries) {
      const name = leave.userFullName;
      if (!techMap.has(name)) {
        techMap.set(name, { userFullName: name, appointments: [], leaveEntries: [] });
      }
      techMap.get(name)!.leaveEntries.push(leave);
    }

    return Array.from(techMap.values()).sort((a, b) =>
      a.userFullName.localeCompare(b.userFullName),
    );
  }, [appointments, leaveEntries]);

  return {
    appointments,
    leaveEntries,
    technicianColumns,
    isLoading,
    error,
    reload: loadData,
  };
}
